import { ClickAwayListener } from "@nova/component-click-away-listener"
import { convertDistance } from "@nova/format"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import classNames from "classnames"
import { animationClass } from "features/data-management/workspace/constants"
import {
    selectIdRenaming,
    selectWorkspaceDistanceUnit,
    setIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect, useMemo, useRef, useState } from "react"
import * as Icons from "@nova/component-icons"
import { DistanceUnit, formattedDistanceUnit } from "@nova/constants"
import {
    SecondaryFlyoutMenu,
    SecondaryFlyoutMenuContentItem,
} from "features/map/inspector-panel/icons/TODO-put-in-package/SecondaryFlyoutMenu"
import { perimeterDistanceOptions } from "features/map/inspector-panel/hotspots/constants"

const DEFAULT_OFFSET_IN_METERS = 3.048 // 10 Feet
const ID = "fp-auto-offset"

export const FirePerimterPluginActionMethodAutoOffsetInput = ({
    onOffsetChanged,
    isOpen,
    setIsOpen,
}: {
    onOffsetChanged: (Offset: number) => void
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
}) => {
    const dispatch = useAppDispatch()
    const idRenaming = useAppSelector(selectIdRenaming())
    const isRenaming = ID === idRenaming

    const workspaceDistanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const [offsetUnit, setOffsetUnit] = useState(workspaceDistanceUnit)

    const layerNameInputRef = useRef<HTMLInputElement>(null)
    const [localOffset, setLocalOffset] = useState(
        Math.floor(
            convertDistance(DEFAULT_OFFSET_IN_METERS, DistanceUnit.Meters, offsetUnit)
        ).toString()
    )

    useEffect(() => {
        if (isRenaming) layerNameInputRef.current?.focus()
        else layerNameInputRef.current?.blur()
    }, [isRenaming])

    // When the unit changes, call onOffsetChanged.
    // The logic for handling when the offset itself changes is in the JSX.
    useEffectUnsafe(() => {
        onOffsetChanged(convertDistance(Number(localOffset), offsetUnit, DistanceUnit.Meters))
    }, [offsetUnit])

    const flyoutContentItems: SecondaryFlyoutMenuContentItem[] = useMemo(
        () =>
            perimeterDistanceOptions.map((option) => ({
                id: option,
                title: formattedDistanceUnit[option].full,
                onClick: () => setOffsetUnit(option),
                isSelected: option === offsetUnit,
            })),
        [offsetUnit]
    )

    return (
        <div className="mt-2 flex flex-col gap-2">
            <h5 className="text-gray-100 select-none">Offset</h5>

            {/* Layer Name Input Field */}
            <ClickAwayListener
                onClickAway={() => {
                    if (isRenaming) dispatch(setIdRenaming(undefined))
                }}
            >
                <div className="flex gap-2">
                    <div
                        className={classNames(
                            "rounded-lg bg-gray-700 h-10 pl-4 pr-2 border cursor-text flex items-center flex-1 w-full min-w-0",
                            animationClass,
                            {
                                "border-transparent": !isRenaming,
                                "border-blue-200": isRenaming,
                            }
                        )}
                        onMouseDown={() => dispatch(setIdRenaming(ID))}
                    >
                        <input
                            ref={layerNameInputRef}
                            type="text"
                            className={classNames(
                                "text-gray-0 placeholder-gray-400",
                                "p-0 bg-transparent border-0 !shadow-none min-w-0 flex-grow",
                                "focus:outline-none focus:border-transparent focus:ring-0",
                                "text-sm" // <h5> text style
                            )}
                            placeholder="0"
                            value={isRenaming ? localOffset : Math.floor(Number(localOffset))}
                            onKeyDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                let sanitizedContents = e.target.value.trimStart()
                                if (!/^$|^(?:\d+(?:\.\d*)?|\.\d+|\.)$/.test(sanitizedContents))
                                    return
                                setLocalOffset(sanitizedContents)

                                if (sanitizedContents === ".") sanitizedContents = "0"
                                onOffsetChanged(
                                    convertDistance(
                                        Number(sanitizedContents),
                                        offsetUnit,
                                        DistanceUnit.Meters
                                    )
                                )
                            }}
                            onBlur={() => {
                                if (localOffset === ".") setLocalOffset("0")
                            }}
                        />
                    </div>

                    {/* Unit Dropdown */}
                    <div className="relative z-10">
                        {/* Dropdown Button */}
                        <div
                            className={classNames(
                                "rounded-md border hover:border-gray-500 flex items-center justify-center text-gray-0 pl-2 pr-0.5 group cursor-pointer h-10",
                                {
                                    "border-transparent": !isOpen,
                                    "border-gray-500 bg-gray-500": isOpen,
                                }
                            )}
                            onClick={() => {
                                setIsOpen(!isOpen)
                            }}
                        >
                            <small className="select-none">
                                {formattedDistanceUnit[offsetUnit].abbrev}
                            </small>
                            <Icons.SmallIcon
                                Icon={Icons.ArrowDropdown}
                                className={classNames("group-hover:text-gray-0", {
                                    "text-gray-300": !isOpen,
                                    "text-gray-0": isOpen,
                                })}
                            />
                        </div>

                        {/* Actual Dropdown */}
                        {isOpen && (
                            <div
                                className="absolute right-0"
                                style={{ top: "calc(100% + 0.25rem)" }}
                            >
                                <SecondaryFlyoutMenu
                                    className="whitespace-nowrap"
                                    items={flyoutContentItems}
                                    onBlur={() => setIsOpen(false)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ClickAwayListener>
        </div>
    )
}
